import React, { useCallback, useEffect, useMemo, useRef } from 'react';

type OverrideSetter<T> = React.Dispatch<React.SetStateAction<Record<string, T>>>;

interface EnqueueArgs<T> {
    key: string;
    next: T;
    write: () => Promise<void>;
    onError: (err: any) => void;
}

/**
 * Reviewer-owned overrides on top of the server's values: the screen echoes instantly, the
 * network is serialized and coalesced per key, and a failed write drops the override so the
 * value falls back to what the server holds.
 *
 * Rolling back means *deleting* the override, always: the container derives
 * `override ?? server`, so the server's value is reached by getting out of the way rather
 * than by keeping a copy of it.
 *
 * That only works because success does the other half — `onAcked` records the value in the
 * payload, and the override is then dropped too. Two earlier shapes of this each kept the
 * acknowledged value twice: one folded into the payload but never dropped the override, the
 * other dropped the override but kept a private `acked` ref that no re-read invalidated.
 * Folding *and* dropping is what leaves exactly one copy, in the half of the state a re-read
 * replaces wholesale — so a correction made elsewhere supersedes ours by construction
 * instead of being masked by it (SKIN-280).
 *
 * Two things this design also has to get right, both learned the expensive way:
 *
 * - **The screen applies at enqueue time, never behind the network.** The comment field is
 *   fully controlled with no local buffer, so deferring the echo re-renders it backwards
 *   mid-word: the text snaps, the caret jumps, and the server stores whatever survived —
 *   typing "blur" can persist "blr".
 * - **Coalesced, not FIFO.** That field emits per keystroke, so a strict queue is one
 *   request per character. A newer enqueue replaces the pending one: at most one request in
 *   flight and one waiting.
 *
 * A failed write does not drop the override when a newer value is already pending — that
 * value is what the reviewer is looking at and is about to be sent. The error is reported
 * either way, unless the screen is gone.
 *
 * `enqueue`'s promise tracks the *drain*, not this call's request: coalesced calls get an
 * already-resolved promise back, because their value has not been sent and may be superseded
 * before it is. Awaiting it never means "saved".
 */
export function useSerializedKeyedWrites<T>(
    setOverrides: OverrideSetter<T>,
    /**
     * Record a landed write where the server's facts live. Taken at construction, not per
     * call: nothing about it varies between writes for one map, and the override is dropped
     * immediately after it runs — so folding and dropping, the two halves of one invariant,
     * are both owned here instead of one of them being re-supplied by every caller.
     */
    onAcked: (key: string, value: T) => void,
) {
    // Held in a ref so it never enters the memo chain below: a caller passing an inline
    // arrow would otherwise make `enqueue` a new function every render, and the returned
    // object's stability is what keeps the container's effects from looping.
    const ackedHandler = useRef(onAcked);
    ackedHandler.current = onAcked;
    /** Newest value not yet sent, per key. At most one. */
    const pending = useRef(new Map<string, EnqueueArgs<T>>());
    const draining = useRef(new Set<string>());
    const alive = useRef(true);

    useEffect(() => () => { alive.current = false; }, []);

    const setOverride = useCallback((key: string, value: T) => {
        if (!alive.current) return;
        setOverrides((current) => ({ ...current, [key]: value }));
    }, [setOverrides]);

    const dropOverride = useCallback((key: string) => {
        if (!alive.current) return;
        setOverrides((current) => {
            if (!(key in current)) return current;
            const next = { ...current };
            delete next[key];
            return next;
        });
    }, [setOverrides]);

    const drain = useCallback(async (key: string): Promise<void> => {
        draining.current.add(key);
        try {
            for (;;) {
                const job = pending.current.get(key);
                if (!job) return;
                pending.current.delete(key);
                try {
                    await job.write();
                    if (!alive.current) continue;
                    ackedHandler.current(key, job.next);
                    // Dropped right away, so the acknowledged value lives in the payload and
                    // nowhere else. Guarded like the failure path: with a newer value pending,
                    // removing the override would show the payload behind it and snap a
                    // controlled field backwards mid-word.
                    if (!pending.current.has(key)) dropOverride(key);
                } catch (err: any) {
                    if (!alive.current) continue;
                    job.onError(err);
                    if (!pending.current.has(key)) {
                        dropOverride(key);
                    }
                }
            }
        } finally {
            draining.current.delete(key);
        }
    }, [dropOverride]);

    const enqueue = useCallback((args: EnqueueArgs<T>): Promise<void> => {
        setOverride(args.key, args.next);
        pending.current.set(args.key, args);
        if (draining.current.has(args.key)) {
            return Promise.resolve();
        }
        return drain(args.key);
    }, [setOverride, drain]);

    // Memoized: callers put this in `useCallback`/`useEffect` deps, and a fresh object per
    // render turns those into an infinite render loop.
    return useMemo(() => ({ enqueue }), [enqueue]);
}
